/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import type { Category, Tag } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { MobileSearchBarTab } from '@eliadmin/core/src/components/header/search-bar/mobile-search-bar-tab';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { SheetTrigger } from '@eliadmin/core/src/components/ui/sheet';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { LuX } from 'react-icons/lu';
import { Paragraph } from './paragraph';
import Link from '@eliadmin/core/src/components/link';
import { IoIosSearch } from 'react-icons/io';

type GenericItem = {
  name: string;
  slug: string;
};

export type SearchBarProps = {
  searchAnalytics?: SearchAnalyticsData;
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  isLoading: boolean;
  shouldRedirect?: boolean;
  items: React.ReactNode[];
  itemsBlogs?: Blog[];
  itemsTags?: any[];
  itemsCategories?: any[];
  placeholder?: string;
  customButton?: React.ReactNode;
  desktopSearchButton?: boolean;
  mobileSearchButton?: boolean;
  disableSidebar?: boolean;
  className?: {
    root?: string;
    form?: string;
    input?: InputProps['className'];
    button?: string;
    popoverContent?: string;
    spinner?: string;
    itemsClassName?: string;
    itemClassName?: string;
    empty?: string;
    emptyText?: string;
  };
  toggle?: () => void;
  whiteText: boolean;
};

export function SearchBar({
  className,
  setSearchQuery,
  searchQuery,
  isLoading,
  items,
  itemsBlogs,
  itemsTags,
  itemsCategories,
  placeholder,
  shouldRedirect = false,
  customButton,
  mobileSearchButton = false,
  desktopSearchButton = false,
  disableSidebar = false,
  whiteText,
  toggle,
  searchAnalytics,
}: SearchBarProps) {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  const [debounceFinished, setDebounceFinished] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | boolean = false) => {
    if (!searchQuery.trim()) return;

    if (typeof e === 'object') {
      e.preventDefault();
    }

    if (!searchQuery || searchQuery === '') {
      router.push(shouldRedirect ? ROUTES.SEARCH() : `${pathname}`);
      return;
    }

    if (toggle) {
      toggle();
    }

    router.push(shouldRedirect ? ROUTES.SEARCH(searchQuery) : `${pathname}?search=${searchQuery}`);
  };

  useEffect(() => {
    setIsFocused(false);
  }, [pathname]);

  useEffect(() => {
    if (!isFocused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFocused(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFocused]);

  useEffect(() => {
    if (!isFocused) return;
    const onMouseDown = (e: MouseEvent) => {
      if (searchBarRef.current?.contains(e.target as Node)) return;
      setIsFocused(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isFocused]);

  useEffect(() => {
    setDebounceFinished(false);
    const timeoutId = setTimeout(() => setDebounceFinished(true), 400);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, isLoading]);

  const handleClose = () => setIsFocused(false);

  const itemCategories = itemsCategories as Category[] | undefined;
  const itemTags = itemsTags as Tag[] | undefined;
  const itemBlogs = itemsBlogs as Blog[] | undefined;

  const hasSidebarContent =
    (itemCategories?.length ?? 0) > 0 ||
    (itemTags?.length ?? 0) > 0 ||
    (itemBlogs?.length ?? 0) > 0;

  const tabsTriggerClassName = cn(
    'rounded-none pb-3 flex items-center justify-center border-b-[3px] border-b-transparent text-sm font-semibold tracking-tight pt-0',
    'data-[state=active]:text-primary data-[state=active]:border-b-primary data-[state=active]:shadow-none',
    'data-[state=inactive]:text-[#666666]',
  );

  const renderMobileTabs = () => (
    <Tabs
      defaultValue="products"
      className="flex h-full min-h-0 w-full flex-col gap-3 p-0 shadow-none"
    >
      <TabsList
        className={cn(
          'grid h-fit w-full grid-cols-4 gap-0 rounded-none bg-transparent p-0 shadow-none',
          'ea-search-bar-tabs__list',
        )}
      >
        <TabsTrigger value="products" className={cn(tabsTriggerClassName)}>
          {t('text150')}
        </TabsTrigger>
        <TabsTrigger value="blogs" className={cn(tabsTriggerClassName)}>
          {t('text671')}
        </TabsTrigger>
        <TabsTrigger value="tags" className={cn(tabsTriggerClassName)}>
          {t('text437')}
        </TabsTrigger>
        <TabsTrigger value="categories" className={cn(tabsTriggerClassName)}>
          {t('text176')}
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="products"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 shadow-none [-webkit-overflow-scrolling:touch]"
      >
        {items && (
          <SearchAnalyticsBoundary data={searchAnalytics} mode="autocomplete" active={!isLoading}>
            <div className="w-full">
              {isLoading ? (
                <div
                  className={cn(
                    'flex h-[300px] w-full items-center justify-center',
                    'ea-search-bar__spinner',
                    className?.spinner,
                  )}
                >
                  <Spinner />
                </div>
              ) : items.length ? (
                <div
                  className={cn(
                    'flex w-full flex-col items-center justify-start gap-3',
                    'ea-search-bar__items',
                    className?.itemsClassName,
                  )}
                >
                  {items.slice(0, 4).map((item, searchIndex) => (
                    <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                      {item &&
                        React.cloneElement(item as any, {
                          className: cn('', className?.itemClassName),
                        })}
                    </SearchAnalyticsItem>
                  ))}
                  <SheetTrigger asChild className="my-3">
                    <Button
                      variant={'secondary'}
                      type="button"
                      onClick={(e) =>
                        handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>)
                      }
                      className={cn('w-full sm:w-fit', 'ea-search-bar__button', className?.button)}
                    >
                      {t('text3745')}
                    </Button>
                  </SheetTrigger>
                </div>
              ) : (
                <div className={cn('flex flex-col p-4', 'ea-search-bar__empty', className?.empty)}>
                  <p className={cn('text-sm', 'ea-search-bar__empty-text', className?.emptyText)}>
                    {t('text554')}
                  </p>
                </div>
              )}
            </div>
          </SearchAnalyticsBoundary>
        )}
      </TabsContent>
      <TabsContent
        value="blogs"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'blogs'} items={itemBlogs} toggle={toggle} />
      </TabsContent>
      <TabsContent
        value="tags"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'tags'} items={itemTags} toggle={toggle} />
      </TabsContent>
      <TabsContent
        value="categories"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'categories'} items={itemCategories} toggle={toggle} />
      </TabsContent>
    </Tabs>
  );

  if (mobileSearchButton) {
    return (
      <div className={cn('flex w-full flex-col items-start justify-start', className?.root)}>
        <form
          role="search"
          aria-label={systemLabels('search')}
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!searchQuery.trim()) {
              toggle && toggle();
              return;
            }
            handleSubmit(e);
          }}
          className={cn('flex w-full items-center gap-3 px-4 pb-3 pt-6', 'ea-search-bar__form')}
        >
          <Input
            aria-label={systemLabels('search')}
            before={<IoIosSearch size={18} className="text-[#9E9E9E]" />}
            value={searchQuery}
            placeholder={placeholder ?? t('text162')}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant={HEADER_CONFIG.searchBar.inputVariant}
            className={{ main: 'h-[60px] sm:h-[60px]', wrapper: 'h-[60px] sm:h-[60px] ' }}
          />
          <Button
            aria-label={systemLabels('search')}
            type="button"
            variant={'none'}
            size={'none'}
            onClick={() => {
              toggle && toggle();
            }}
            className={cn(
              'max-h-6 min-h-6 min-w-6 max-w-6 shrink-0',
              'ea-mobile-header-search-bar__button',
              className?.button,
            )}
          >
            <LuX className="h-6 w-6" />
          </Button>
        </form>
        {searchQuery.length > 1 && (
          <div
            className={cn(
              'mt-3 w-full min-h-0 flex-1 overflow-hidden bg-transparent',
              'ea-search-bar__popover-content',
              className?.popoverContent,
            )}
          >
            {renderMobileTabs()}
          </div>
        )}
      </div>
    );
  }

  const showResults = isFocused && searchQuery.length > 1;

  return (
    <div
      ref={searchBarRef}
      className={cn('relative hidden w-full md:flex flex-col', 'ea-search-bar', className?.root)}
    >
      <form
        role="search"
        aria-label={systemLabels('search')}
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn('flex h-[60px] w-full flex-row items-center', 'ea-search-bar__form')}
      >
        <Input
          aria-label={systemLabels('search')}
          onFocus={() => setIsFocused(true)}
          value={searchQuery}
          placeholder={t('text3834')}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant={'transparent'}
          className={{
            main: 'placeholder:[#646B96] h-[60px] rounded-full px-6 pr-24 sm:h-[60px]',
            wrapper: 'h-[60px] rounded-full sm:h-[60px]',
            after: 'pr-1',
          }}
          after={
            <Button
              aria-label={systemLabels('search')}
              type="submit"
              className="shrink-0 whitespace-nowrap"
            >
              <ButtonIcon alt={t('text1')} url="/images/icons/header/search-white.svg" />
              {t('text1')}
            </Button>
          }
        />
      </form>

      {showResults && (
        <SearchAnalyticsBoundary data={searchAnalytics} mode="autocomplete" active={!isLoading}>
          <div
            className={cn(
              'absolute left-1/2 top-full z-50 mt-1 w-[1200px] max-w-[100vw] -translate-x-1/2 overflow-hidden rounded-[20px] border bg-white [box-shadow:0px_4px_16px_0px_rgba(0,0,0,0.06),0px_12px_40px_-4px_rgba(0,0,0,0.10)]',
              'ea-search-bar__popover-content',
              className?.popoverContent,
            )}
          >
            {(isLoading || !debounceFinished) && (
              <div
                className={cn(
                  'flex h-24 w-full items-center justify-center',
                  'ea-search-bar__spinner',
                  className?.spinner,
                )}
              >
                <Spinner className="h-6 w-6" />
              </div>
            )}
            {!isLoading && debounceFinished && !items?.length && !hasSidebarContent && (
              <div className={cn('p-4', 'ea-search-bar__empty', className?.empty)}>
                <Paragraph color="muted-foreground" className="w-full text-center" size="lg">
                  {t('text752')}
                </Paragraph>
              </div>
            )}
            {!isLoading && debounceFinished && (items?.length > 0 || hasSidebarContent) && (
              <div className="flex w-full flex-col items-start justify-start gap-4 p-4 sm:p-5 md:flex-row md:items-stretch md:justify-between lg:gap-6 lg:p-6">
                {items?.length > 0 && (
                  <div
                    className={cn(
                      'flex w-full flex-col items-start justify-start gap-3',
                      'ea-search-bar__items',
                      className?.itemsClassName,
                    )}
                  >
                    {items.slice(0, 4).map((item, searchIndex) => (
                      <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                        {item &&
                          React.cloneElement(item as any, {
                            className: cn('', className?.itemClassName),
                          })}
                      </SearchAnalyticsItem>
                    ))}
                    <div className="mt-2 flex w-full items-center justify-center">
                      <Button
                        aria-label={systemLabels('search')}
                        variant="secondary"
                        className="w-full sm:w-fit"
                        onClick={handleClose}
                      >
                        <Link href={ROUTES.SEARCH(searchQuery)}>{t('text3745')}</Link>
                      </Button>
                    </div>
                  </div>
                )}
                {hasSidebarContent && (
                  <div className="w-full md:max-w-[280px] lg:max-w-[403px]">
                    <div className="flex h-full w-full flex-col items-start justify-start gap-6 rounded-[20px] bg-[#F3F4F6] p-4 sm:p-5 lg:p-6">
                      <GenericItems
                        items={itemCategories}
                        type="categories"
                        onNavigate={handleClose}
                      />
                      <GenericItems
                        items={itemTags as GenericItem[] | undefined}
                        type="tags"
                        onNavigate={handleClose}
                      />
                      <GenericItems
                        items={itemBlogs as GenericItem[] | undefined}
                        type="blogs"
                        onNavigate={handleClose}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SearchAnalyticsBoundary>
      )}
    </div>
  );
}

function GenericItems({
  items,
  type,
  onNavigate,
}: {
  items: GenericItem[] | undefined;
  type: 'categories' | 'tags' | 'blogs';
  onNavigate?: () => void;
}) {
  if (!items?.length) return null;

  const title = type === 'categories' ? 'Kategórie' : type === 'tags' ? 'Značky' : 'Blog';

  return (
    <div className="flex w-full flex-col items-start justify-start gap-2">
      <p className="text-sm font-medium tracking-tighter text-foreground">{title}</p>
      {items.map((item, searchIndex) => (
        <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
          {
            <Link
              key={item.name}
              prefetch={false}
              href={item.slug}
              onClick={() => onNavigate?.()}
              className="generic-hover line-clamp-2 text-sm font-medium text-primary lg:text-base"
            >
              {item.name}
            </Link>
          }
        </SearchAnalyticsItem>
      ))}
    </div>
  );
}
